/**
 * RightDrawerShell — shared layout shell for mobile right-side drawers.
 * Lives in packages/ui as single source of truth.
 * Apps pass content via children/header/footer slots.
 *
 * Does NOT import any app code.
 */
import * as React from 'react';
import { cn } from '../../lib/utils';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useIsMobile } from '../../hooks/useIsMobile';
import { X } from 'lucide-react';

export type RightDrawerVariant = 'account' | 'filters' | 'search' | 'custom';

export interface RightDrawerShellProps {
  open: boolean;
  variant: RightDrawerVariant;
  onClose?: () => void;

  /** e.g. "bg-card" or "bg-muted" — overrides default per-variant background */
  surfaceClassName?: string;

  /** drawer width classes — default "w-[85vw] max-w-[360px]" */
  widthClassName?: string;

  /** Override the default variant label in the header */
  title?: string;

  /** optional title row — if omitted, a default header with variant name + X is rendered */
  header?: React.ReactNode;

  /** main scrollable content */
  children: React.ReactNode;

  /** optional sticky footer (Apply / Search buttons etc.) */
  footer?: React.ReactNode;

  /** mobile-only scroll lock — default true */
  lockScroll?: boolean;

  /** top offset in px — default 57 (navbar height) */
  topOffset?: number;
}

const VARIANT_LABELS: Record<RightDrawerVariant, string> = {
  account: 'Account',
  filters: 'Filters',
  search: 'Search',
  custom: '',
};

const VARIANT_BG: Record<RightDrawerVariant, string> = {
  account: 'bg-card',
  filters: 'bg-background',
  search: 'bg-background',
  custom: 'bg-card',
};

export const RightDrawerShell: React.FC<RightDrawerShellProps> = ({
  open,
  variant,
  onClose,
  surfaceClassName,
  widthClassName = 'w-[85vw] max-w-[360px]',
  title,
  header,
  children,
  footer,
  lockScroll = true,
  topOffset = 57,
}) => {
  const isMobile = useIsMobile();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLElement>(null);

  // Lock background scroll on mobile only
  useBodyScrollLock(open && isMobile && lockScroll);

  // Blur focused element inside panel when closing + apply inert
  React.useEffect(() => {
    if (!open && panelRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    rootRef.current?.toggleAttribute('inert', !open);
  }, [open]);

  const bg = surfaceClassName ?? VARIANT_BG[variant];

  const defaultHeader = (
    <div className={cn('shrink-0 flex items-center justify-between px-4 py-3 border-b border-border', bg)}>
      <span className="text-sm font-semibold text-foreground">
        {title ?? VARIANT_LABELS[variant]}
      </span>
      {onClose && (
        <button
          onClick={onClose}
          className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label={`Close ${variant} panel`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={cn(
        'md:hidden fixed left-0 right-0 z-50 transition-opacity duration-200',
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
      )}
      style={{ top: topOffset, bottom: 0, height: `calc(100dvh - ${topOffset}px)` }}
      aria-hidden={!open}
      data-right-drawer-shell="true"
      data-variant={variant}
    >
      {/* Scrim */}
      {onClose && (
        <div className="absolute inset-0 z-0 bg-black/40" onClick={onClose} />
      )}

      {/* Panel surface */}
      <aside
        ref={panelRef}
        className={cn(
          'absolute right-0 top-0 h-full border-l border-border shadow-xl flex flex-col overflow-hidden z-10',
          'transition-transform duration-250 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
          widthClassName,
          bg,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {header ?? defaultHeader}

        {/* Scrollable body — inherits same bg */}
        <div className={cn('flex-1 overflow-y-auto overflow-x-hidden', bg)}>
          {children}
        </div>

        {/* Optional footer */}
        {footer && (
          <div className={cn('shrink-0 sticky bottom-0 border-t border-border', bg)}>
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
};

RightDrawerShell.displayName = 'RightDrawerShell';
